import React, { useCallback, useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Paper, Tab, Tabs, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import GenericDataGrid from '../../../components/genericTable';
import NotificationSnackbar from '../../../components/notificationSnackbar';
import { createManagementTableSx } from '../../../styles/tableStyles';
import {
    managementCreateButtonSx,
    managementHeaderActionSx,
    managementHeaderContentSx,
    managementHeaderSx,
    managementPageSubtitleSx,
} from '../../../styles/managementStyles';
import { buttonContentSx } from '../../../styles/dialogStyles';
import { compactTableActionButtonSx, tableDeleteButtonSx, tableViewButtonSx } from '../../../styles/actionStyles';
import { RainfallBaselineModal, SoilMoistureThresholdModal } from './baselineModals';
import {
    RAINFALL_BANDS,
    SEASONS,
    SIGNAL_WEIGHT,
    SOIL_MOISTURE_BANDS,
    SOIL_MOISTURE_WINDOW_DAYS,
    deleteRainfallBaseline,
    deleteSoilMoistureThreshold,
    getRainfallBaselines,
    getSoilMoistureThresholds,
    rainfallBandEdges,
    seasonLabel,
} from './climate.service';

const textCellSx = {
    fontSize: 16,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    color: '#1C1B1F',
};

const cardSx = {
    p: 3,
    borderRadius: '16px',
    border: '1px solid #DFE5F6',
    backgroundColor: '#FFFFFF',
};

const captionSx = { fontSize: 12, letterSpacing: '0.4px', textTransform: 'uppercase', color: '#625B71', fontWeight: 600 };

function formatDate(value) {
    if (!value) return '-';
    return new Date(value).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function riskColor(risk) {
    if (risk <= 3) return '#4F8506';
    if (risk <= 6) return '#8A5A00';
    return '#DA342E';
}

function BandTable({ title, bands }) {
    return (
        <Box>
            <Typography sx={{ ...captionSx, mb: 1 }}>{title}</Typography>
            {bands.map((band) => (
                <Box
                    key={band.label}
                    sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, py: 0.5, flexWrap: 'wrap' }}
                >
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: riskColor(band.risk), minWidth: 28 }}>
                        {band.risk.toFixed(1)}
                    </Typography>
                    <Typography sx={{ fontSize: 15, color: '#1C1B1F' }}>{band.label}</Typography>
                    <Typography sx={{ fontSize: 14, color: '#625B71' }}>{band.detail}</Typography>
                </Box>
            ))}
        </Box>
    );
}

function EmptyState({ signal, what, onAdd, addLabel }) {
    return (
        <Paper elevation={0} sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
            <Icon icon="tabler:cloud-off" width="40" height="40" color="#94A3B8" />
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1C1B1F', mt: 2, mb: 1 }}>
                No {what} configured
            </Typography>
            <Typography sx={{ color: '#625B71', maxWidth: 620, mx: 'auto', mb: 3 }}>
                <strong>{signal}</strong> is skipped for every farmer until a row exists here. The engine reports the
                signal as not-applicable rather than guessing a value, so scores stay honest — they just carry less
                weather evidence, which lowers confidence rather than the score.
            </Typography>
            <Button variant="contained" onClick={onAdd} sx={{ ...managementCreateButtonSx, minWidth: 220 }}>
                <Box component="span" sx={buttonContentSx}>
                    <Icon icon="tabler:plus" width="18" height="18" />
                    <span>{addLabel}</span>
                </Box>
            </Button>
        </Paper>
    );
}

/**
 * Climate reference data for the WEATHER half of eK Score.
 *
 * Two signals need a reference value the platform cannot derive: a state's long-term seasonal
 * rainfall (C1) and a crop's root-zone moisture requirement (F5). Both tables ship empty on purpose
 * — the migration calls populating them an agronomy decision, not an engineering one — so this
 * screen's job is to make the empty state legible and let someone with the figures enter them,
 * showing what each number will do to a score before it is saved.
 */
function Climate() {
    const [tab, setTab] = useState(0);
    const [baselines, setBaselines] = useState([]);
    const [thresholds, setThresholds] = useState([]);
    const [loading, setLoading] = useState(true);

    const [rainfallModalOpen, setRainfallModalOpen] = useState(false);
    const [editingBaseline, setEditingBaseline] = useState(null);
    const [thresholdModalOpen, setThresholdModalOpen] = useState(false);
    const [editingThreshold, setEditingThreshold] = useState(null);

    const [rainfallPage, setRainfallPage] = useState(0);
    const [rainfallSize, setRainfallSize] = useState(10);
    const [thresholdPage, setThresholdPage] = useState(0);
    const [thresholdSize, setThresholdSize] = useState(10);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
    const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

    const load = useCallback(async () => {
        setLoading(true);
        const [rain, soil] = await Promise.all([getRainfallBaselines(), getSoilMoistureThresholds()]);
        setBaselines(rain.success ? rain.data : []);
        setThresholds(soil.success ? soil.data : []);
        setLoading(false);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleDeleteBaseline = async (id) => {
        const result = await deleteRainfallBaseline(id);
        if (!result.success) {
            notify(result.error.message, 'error');
            return;
        }
        await load();
        notify('Rainfall baseline removed.');
    };

    const handleDeleteThreshold = async (id) => {
        const result = await deleteSoilMoistureThreshold(id);
        if (!result.success) {
            notify(result.error.message, 'error');
            return;
        }
        await load();
        notify('Soil moisture threshold removed.');
    };

    const rainfallColumns = [
        {
            field: 'state',
            headerName: 'State',
            flex: 1.1,
            minWidth: 160,
            sortable: false,
            renderCell: (params) => (
                <Typography sx={{ ...textCellSx, fontWeight: 600 }}>{params.value}</Typography>
            ),
        },
        {
            field: 'seasonCode',
            headerName: 'Season',
            flex: 0.9,
            minWidth: 140,
            sortable: false,
            renderCell: (params) => <Typography sx={textCellSx}>{seasonLabel(params.value)}</Typography>,
        },
        {
            field: 'baselineSeasonalRainfallMm',
            headerName: 'Baseline',
            flex: 0.8,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => (
                <Typography sx={{ ...textCellSx, fontWeight: 600 }}>{params.value} mm</Typography>
            ),
        },
        {
            field: 'bands',
            headerName: 'Deficit thresholds',
            flex: 1.3,
            minWidth: 220,
            sortable: false,
            renderCell: (params) => {
                const edges = rainfallBandEdges(params.row.baselineSeasonalRainfallMm);
                return (
                    <Typography sx={{ ...textCellSx, fontSize: 14, color: '#625B71' }}>
                        &lt;{Math.round(edges.mild)} mm → 3 · &lt;{Math.round(edges.marked)} mm → 6
                    </Typography>
                );
            },
        },
        {
            field: 'effectiveFrom',
            headerName: 'Effective from',
            flex: 0.9,
            minWidth: 140,
            sortable: false,
            renderCell: (params) => <Typography sx={textCellSx}>{formatDate(params.value)}</Typography>,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                    <Button
                        sx={{ ...tableViewButtonSx, ...compactTableActionButtonSx }}
                        onClick={() => {
                            setEditingBaseline(params.row);
                            setRainfallModalOpen(true);
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        sx={{ ...tableDeleteButtonSx, ...compactTableActionButtonSx }}
                        onClick={() => handleDeleteBaseline(params.row.id)}
                    >
                        Delete
                    </Button>
                </Box>
            ),
        },
    ];

    const thresholdColumns = [
        {
            field: 'cropName',
            headerName: 'Crop',
            flex: 1.2,
            minWidth: 180,
            sortable: false,
            renderCell: (params) => (
                <Typography sx={{ ...textCellSx, fontWeight: 600 }}>{params.value}</Typography>
            ),
        },
        {
            field: 'requiredThreshold',
            headerName: 'Required GWETROOT',
            flex: 1,
            minWidth: 170,
            sortable: false,
            renderCell: (params) => (
                <Typography sx={{ ...textCellSx, fontWeight: 600 }}>{Number(params.value).toFixed(2)}</Typography>
            ),
        },
        {
            field: 'bands',
            headerName: 'Shortfall thresholds',
            flex: 1.4,
            minWidth: 240,
            sortable: false,
            renderCell: (params) => {
                const required = Number(params.row.requiredThreshold);
                return (
                    <Typography sx={{ ...textCellSx, fontSize: 14, color: '#625B71' }}>
                        &lt;{(required * 0.8).toFixed(2)} → 7 · &lt;{(required * 0.5).toFixed(2)} → 10
                    </Typography>
                );
            },
        },
        {
            field: 'notes',
            headerName: 'Notes',
            flex: 1.4,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => (
                <Typography sx={{ ...textCellSx, fontSize: 14, color: '#625B71', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {params.value || '-'}
                </Typography>
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                    <Button
                        sx={{ ...tableViewButtonSx, ...compactTableActionButtonSx }}
                        onClick={() => {
                            setEditingThreshold(params.row);
                            setThresholdModalOpen(true);
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        sx={{ ...tableDeleteButtonSx, ...compactTableActionButtonSx }}
                        onClick={() => handleDeleteThreshold(params.row.id)}
                    >
                        Delete
                    </Button>
                </Box>
            ),
        },
    ];

    const showingRainfall = tab === 0;
    const addLabel = showingRainfall ? 'ADD BASELINE' : 'ADD THRESHOLD';

    const openAdd = () => {
        if (showingRainfall) {
            setEditingBaseline(null);
            setRainfallModalOpen(true);
        } else {
            setEditingThreshold(null);
            setThresholdModalOpen(true);
        }
    };

    return (
        <>
            <Box sx={managementHeaderSx}>
                <Box sx={managementHeaderContentSx}>
                    <Typography sx={managementPageSubtitleSx}>
                        Reference baselines the eK Score weather signals compare live NASA POWER observations
                        against. Both ship empty and are configured per environment.
                    </Typography>
                </Box>
                {(showingRainfall ? baselines.length : thresholds.length) > 0 && (
                    <Box sx={managementHeaderActionSx}>
                        <Button
                            variant="contained"
                            onClick={openAdd}
                            sx={{ ...managementCreateButtonSx, width: { xs: '100%', md: 'auto' }, minWidth: { xs: '100%', md: 204 } }}
                        >
                            <Box component="span" sx={buttonContentSx}>
                                <Icon icon="tabler:plus" width="18" height="18" />
                                <span>{addLabel}</span>
                            </Box>
                        </Button>
                    </Box>
                )}
            </Box>

            <Tabs
                value={tab}
                onChange={(_, next) => setTab(next)}
                sx={{
                    mb: 3,
                    borderBottom: '1px solid #E7ECF2',
                    '& .MuiTab-root': { textTransform: 'none', fontSize: 16, fontWeight: 500, color: '#625B71' },
                    '& .Mui-selected': { color: '#4F8506 !important' },
                    '& .MuiTabs-indicator': { backgroundColor: '#4F8506' },
                }}
            >
                <Tab label={`Rainfall baselines (${baselines.length})`} />
                <Tab label={`Soil moisture thresholds (${thresholds.length})`} />
            </Tabs>

            {showingRainfall ? (
                <>
                    <Paper elevation={0} sx={{ ...cardSx, mb: 3 }}>
                        <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#1C1B1F', mb: 0.5 }}>
                            C1_RAINFALL_DEFICIT
                        </Typography>
                        <Typography sx={{ color: '#625B71', mb: 2.5 }}>
                            Sums NASA POWER daily rainfall from the start of the current season to today for the
                            farmer's plot, then compares that total against the baseline for the plot's state and
                            season. Enters the Weather category with weight {SIGNAL_WEIGHT}.
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                            <BandTable title="Risk value by deficit" bands={RAINFALL_BANDS} />
                            <Box>
                                <Typography sx={{ ...captionSx, mb: 1 }}>Season windows</Typography>
                                {SEASONS.map((season) => (
                                    <Box key={season.code} sx={{ display: 'flex', gap: 1.5, py: 0.5 }}>
                                        <Typography sx={{ fontSize: 15, color: '#1C1B1F', minWidth: 60 }}>
                                            {season.label}
                                        </Typography>
                                        <Typography sx={{ fontSize: 15, color: '#625B71' }}>
                                            {season.window} · counts from {season.startsOn}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Paper>

                    {baselines.length === 0 && !loading ? (
                        <EmptyState
                            signal="C1_RAINFALL_DEFICIT"
                            what="rainfall baselines"
                            addLabel="ADD BASELINE"
                            onAdd={openAdd}
                        />
                    ) : (
                        <GenericDataGrid
                            rows={baselines.slice(rainfallPage * rainfallSize, rainfallPage * rainfallSize + rainfallSize)}
                            columns={rainfallColumns}
                            rowCount={baselines.length}
                            page={rainfallPage}
                            pageSize={rainfallSize}
                            loading={loading}
                            dataGridSx={createManagementTableSx()}
                            tableMinWidth={1080}
                            rowHeight={60}
                            onPaginationChange={(page, size) => {
                                setRainfallPage(page);
                                setRainfallSize(size);
                            }}
                        />
                    )}
                </>
            ) : (
                <>
                    <Paper elevation={0} sx={{ ...cardSx, mb: 3 }}>
                        <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#1C1B1F', mb: 0.5 }}>
                            F5_SOIL_MOISTURE_ADEQUACY
                        </Typography>
                        <Typography sx={{ color: '#625B71', mb: 2.5 }}>
                            Averages NASA POWER root-zone soil wetness (GWETROOT) over the trailing{' '}
                            {SOIL_MOISTURE_WINDOW_DAYS} days and divides it by the crop's required threshold. Enters
                            the Weather category with weight {SIGNAL_WEIGHT}. A crop with no threshold is skipped, so
                            this list should cover the crops farmers are actually scored on.
                        </Typography>
                        <BandTable title="Risk value by ratio" bands={SOIL_MOISTURE_BANDS} />
                    </Paper>

                    {thresholds.length === 0 && !loading ? (
                        <EmptyState
                            signal="F5_SOIL_MOISTURE_ADEQUACY"
                            what="soil moisture thresholds"
                            addLabel="ADD THRESHOLD"
                            onAdd={openAdd}
                        />
                    ) : (
                        <GenericDataGrid
                            rows={thresholds.slice(thresholdPage * thresholdSize, thresholdPage * thresholdSize + thresholdSize)}
                            columns={thresholdColumns}
                            rowCount={thresholds.length}
                            page={thresholdPage}
                            pageSize={thresholdSize}
                            loading={loading}
                            dataGridSx={createManagementTableSx()}
                            tableMinWidth={1020}
                            rowHeight={60}
                            onPaginationChange={(page, size) => {
                                setThresholdPage(page);
                                setThresholdSize(size);
                            }}
                        />
                    )}
                </>
            )}

            <Alert severity="info" sx={{ borderRadius: '16px', mt: 3 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>Set in code, not here</AlertTitle>
                The band cutoffs, the signal weight of {SIGNAL_WEIGHT}, and the heavy-rainfall thresholds that raise a
                distress event (IMD very heavy 115.6 mm, extremely heavy 204.5 mm) are constants in the scoring engine.
                Changing any of them is a backend change. The season windows shown above are the built-in fallback —
                set a country's own calendar under Configuration → Seasons.
            </Alert>

            <RainfallBaselineModal
                open={rainfallModalOpen}
                baseline={editingBaseline}
                existing={baselines}
                onClose={() => setRainfallModalOpen(false)}
                onSaved={async () => {
                    setRainfallModalOpen(false);
                    await load();
                    notify('Rainfall baseline saved.');
                }}
            />

            <SoilMoistureThresholdModal
                open={thresholdModalOpen}
                threshold={editingThreshold}
                existing={thresholds}
                onClose={() => setThresholdModalOpen(false)}
                onSaved={async () => {
                    setThresholdModalOpen(false);
                    await load();
                    notify('Soil moisture threshold saved.');
                }}
            />

            <NotificationSnackbar
                open={snackbar.open}
                onClose={closeSnackbar}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </>
    );
}

export default Climate;
